import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService, BreadcrumbService, ConfirmDialogService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { Router } from '@angular/router';
import { 
  IRendererPool, 
  ListRendererPoolsRequest, 
  SituAnalyticsWebApiClient,
  DeleteRendererPoolRequest 
} from 'situanalytics-webapi-client';
import { MatDialog } from '@angular/material/dialog';
import { RendererPoolDialogComponent } from '../renderer-pool-dialog/renderer-pool-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-renderer-pool-list',
  templateUrl: './renderer-pool-list.component.html',
  styleUrls: ['./renderer-pool-list.component.scss']
})
export class RendererPoolListComponent implements OnInit {
  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  admin = false;
  poolsDataSource = new MatTableDataSource<IRendererPool>();
  displayedColumns: string[] = ['name', 'rendererCount', 'actions'];

  constructor(
    private analyticsApi: SituAnalyticsWebApiClient,
    private authService: AuthService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private dialog: MatDialog,
    private confirmDialog: ConfirmDialogService,
    private snackBar: MatSnackBar
  ) {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
  }

  ngOnInit(): void {
    this.loadRendererPools();
  }

  loadRendererPools(): void {
    this.loading = true;
    const request = new ListRendererPoolsRequest();
    // Set a large page size to get all pools
    request.pageNumber = 1;
    request.pageSize = 100;
    request.includeRenderers = true; // This will include renderer count
    
    this.analyticsApi.listRendererPools(request)
      .then(response => {
        console.log('Renderer pools response:', response);
        if (response.success && response.items) {
          this.poolsDataSource.data = response.items;
          console.log('Loaded pools:', response.items);
        } else {
          this.poolsDataSource.data = [];
        }
      })
      .catch(error => {
        console.error('Error loading renderer pools:', error);
        this.showMessage('Error loading renderer pools');
        this.poolsDataSource.data = [];
      })
      .finally(() => {
        this.loading = false;
      });
  }

  createPool(): void {
    const dialogRef = this.dialog.open(RendererPoolDialogComponent, {
      width: '600px',
      data: { isNew: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadRendererPools();
      }
    });
  }

  editPool(pool: IRendererPool): void {
    const dialogRef = this.dialog.open(RendererPoolDialogComponent, {
      width: '600px',
      data: { pool, isNew: false }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadRendererPools();
      }
    });
  }

  deletePool(pool: IRendererPool): void {
    this.confirmDialog.open({
      title: 'Delete Renderer Pool',
      message: `Are you sure you want to delete the pool "${pool.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      actionIcon: 'delete',
      actionColour: 'warn'
    });

    this.confirmDialog.confirmed().subscribe(result => {
      if (result) {
        this.performDelete(pool);
      }
    });
  }

  private performDelete(pool: IRendererPool): void {
    const request = new DeleteRendererPoolRequest();
    request.id = pool.id;

    this.analyticsApi.deleteRendererPool(request)
      .then(response => {
        if (response.success) {
          this.showMessage('Renderer pool deleted successfully');
          this.loadRendererPools();
        } else {
          this.showMessage('Failed to delete renderer pool');
        }
      })
      .catch(error => {
        console.error('Error deleting renderer pool:', error);
        this.showMessage('Error deleting renderer pool');
      });
  }

  viewPool(pool: IRendererPool): void {
    // Navigate to a detail view or show renderers in this pool
    this.router.navigate(['/admin/renderers'], { queryParams: { poolId: pool.id } });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 3000 });
  }
}